import { Formik } from 'formik'
import { get } from 'lodash'
import { useRouter } from 'next/router'
import React from 'react'
import { ProductList } from '@public/components/ProductsList'
import { Summary } from '@public/components/Summary'
import {
  getProductQuantityInitialValues,
  getProductQuantityValidationSchema,
} from '@public/Showroom/productQuantityUtils'
import { ErrorFocus } from 'components/ErrorFocus'
import Icon, { ICON_SIZE } from 'components/Icon'
import { Flex, Grid } from 'components/Layout'
import { H2, Text } from 'components/Typography'
import { ROUTE } from 'constants/routes'
import { type CartContextInterface } from 'providers/cart'
import { COLOR_INTENT, FONT_SIZE, SPACE } from 'Theme'
import { type CalculatePriceCheckoutQuery } from 'types/graphql-generated'

interface CartViewProps {
  totalPrice?: CalculatePriceCheckoutQuery['calculatePriceCheckout']['priceToPayWithoutShipping']
  retailSubtotalPrice?: CalculatePriceCheckoutQuery['calculatePriceCheckout']['retailSubtotalPrice']
  tradeSubtotalPrice?: CalculatePriceCheckoutQuery['calculatePriceCheckout']['tradeSubtotalPrice']
  cart: CartContextInterface['cart']
  onRemoveCartItem: CartContextInterface['removeItem']
  onSetCartItem: CartContextInterface['setItem']
  isPriceCalculationLoading: boolean
  discountItems?: CalculatePriceCheckoutQuery['calculatePriceCheckout']['discountItems']
  discount?: number
  onConvertToQuote?: () => void
}

export const CartView: React.FC<React.PWC<CartViewProps>> = ({
  onRemoveCartItem,
  onSetCartItem,
  cart,
  totalPrice,
  retailSubtotalPrice,
  tradeSubtotalPrice,
  isPriceCalculationLoading,
  discountItems,
  discount,
  onConvertToQuote,
}) => {
  const router = useRouter()
  const cartItems = get(cart, 'items', null)

  return (
    <Formik
      enableReinitialize
      onSubmit={() => {
        router.push(ROUTE.SHOWROOM_CHECKOUT_CONTACT())
      }}
      initialValues={getProductQuantityInitialValues({ cartItems })}
      validationSchema={getProductQuantityValidationSchema({
        cartItems,
      })}
      validateOnChange
    >
      {({ handleSubmit }) => (
        <Grid
          width="100%"
          mt={SPACE.PX_30}
          mb={SPACE.PX_40}
          gridGap={SPACE.PX_20}
          gridTemplateColumns={{ MOBILE: '1fr', TABLET: '2fr 1fr' }}
        >
          <ErrorFocus />
          <Flex flexDirection="column">
            <H2>Your cart</H2>
            {!!cartItems.length && (
              <ProductList
                cartItems={cartItems}
                onSetCartItem={onSetCartItem}
                onRemoveCartItem={onRemoveCartItem}
                discountItems={discountItems}
              />
            )}
            <Flex
              mt={{ MOBILE: SPACE.PX_5, TABLET: SPACE.PX_10 }}
              alignSelf="center"
              alignItems="center"
            >
              <Icon.Delivery
                size={ICON_SIZE.PX_20}
                color={COLOR_INTENT.GRAY_90}
              />
              <Text ml={SPACE.PX_10} fontSize={FONT_SIZE.PX_12}>
                Shipping options are calculated at checkout
              </Text>
            </Flex>
          </Flex>
          <Summary
            cart={cart}
            total={totalPrice?.base}
            retailSubtotal={retailSubtotalPrice?.base}
            tradeSubtotal={tradeSubtotalPrice?.base}
            currencyIsoCode={totalPrice?.currencyIsoCode}
            isOnShippingStep={false}
            onProceedToCheckout={handleSubmit}
            isPriceCalculationLoading={isPriceCalculationLoading}
            discount={discount}
            discountItems={discountItems}
            onConvertToQuote={onConvertToQuote}
          />
        </Grid>
      )}
    </Formik>
  )
}
